import React, {useCallback, useEffect, useRef, useState} from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import './IntroSlides.css';
import PageButton from '../elements/PageButton/PageButton';
import {INTRO_SLIDES, INTRO_SLIDES_PAGE_ID} from '../../configs/introSlides';
import {splitTextByAccents} from '../../utils/other/splitTextByAccents';
import {sendButtonClickEvent, sendPageEvent} from '../../analytics/gameAnalytics';
import {trackAdGoal, AD_GOALS} from '../../analytics/adTracking';
import {usePageAnalytics} from '../../hooks/analytics/usePageAnalytics';
import {getDeviceKind} from '../../utils/device/getDeviceKind';
import {MENU_SOUND_MANAGER} from '../../scenes/MenuSoundManager';
import CampfireSparks from '../BackgroundEffects/CampfireSparks';

// Искры у колоды крупнее, чем в меню: кадр колоды занимает верх экрана, и на
// его фоне искры прежнего размера читались как шум
const SPARK_SCALE = 1;

// Та же библиотека, что и у карусели фракций (embla): нативные свайпы на
// тачскрине достаются даром, вместе с инерцией и перехватом вертикальной
// прокрутки
const EMBLA_OPTIONS = {loop: false, align: 'center', duration: 22};

/**
 * Вступительная колода из двух слайдов — питч игры тому, кто открыл ее впервые.
 *
 * Показывается ПОВЕРХ главного меню, а не вместо него: за колодой уже грузится
 * витрина с демо-матчем, и человек, закрывший колоду, попадает не на пустой
 * экран, а на живое поле.
 *
 * Колода ничего не решает про то, кому ее показывать и как запомнить показ —
 * это дело владельца (см. App.js): здесь только показ, листание и метрики.
 *
 * Метрики: монтирование (usePageAnalytics), попадание на каждый слайд (в том
 * числе свайпом и по точкам) и каждый клик по кнопке — и в GameAnalytics, и
 * рекламными целями. Колода живет до первого действия игрока, так что для
 * рекламных кампаний это верхняя ступень воронки.
 */
function IntroSlides({onClose}) {
  usePageAnalytics(INTRO_SLIDES_PAGE_ID);

  const [emblaRef, emblaApi] = useEmblaCarousel(EMBLA_OPTIONS);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  // Просмотр слайда засчитывается один раз: иначе метание туда-сюда свайпом
  // ломало бы воронку «сколько человек дошло до второго слайда»
  const viewedSlideIdsRef = useRef(new Set());

  useEffect(() => {
    // Страница под колодой не должна прокручиваться (см. body.intro-slides-open)
    document.body.classList.add('intro-slides-open');
    return () => document.body.classList.remove('intro-slides-open');
  }, []);

  useEffect(() => {
    // Отдельным событием от mount: там уже пять уровней пути, а разбивка по
    // устройству нужна — колода верстается под телефон и десктоп по-разному
    sendPageEvent(INTRO_SLIDES_PAGE_ID, 'open', getDeviceKind());
    const showTimer = setTimeout(() => setIsVisible(true), 30);
    return () => clearTimeout(showTimer);
  }, []);

  const reportSlideView = useCallback((index) => {
    const slide = INTRO_SLIDES[index];
    if (!slide || viewedSlideIdsRef.current.has(slide.id)) return;
    viewedSlideIdsRef.current.add(slide.id);

    sendPageEvent(INTRO_SLIDES_PAGE_ID, 'slide_view', slide.id, getDeviceKind());
    trackAdGoal(AD_GOALS.INTRO_SLIDE_VIEW, {params: {slide: slide.id, index: index + 1}});
  }, []);

  useEffect(() => {
    if (!emblaApi) {
      // Карусель еще не поднялась — первый слайд уже на экране, и его показ
      // засчитывается независимо от нее (дубли отсекает viewedSlideIdsRef)
      reportSlideView(0);
      return undefined;
    }

    const handleSelect = () => {
      const index = emblaApi.selectedSnap();
      setSelectedIndex(index);
      reportSlideView(index);
    };

    handleSelect();
    emblaApi.on('select', handleSelect).on('reinit', handleSelect);

    return () => {
      emblaApi.off('select', handleSelect).off('reinit', handleSelect);
    };
  }, [emblaApi, reportSlideView]);

  const activeSlide = INTRO_SLIDES[selectedIndex] || INTRO_SLIDES[0];
  const isLastSlide = selectedIndex === INTRO_SLIDES.length - 1;

  const handleNextClick = () => {
    trackAdGoal(AD_GOALS.INTRO_NEXT_CLICK, {params: {slide: activeSlide?.id}});
    emblaApi?.goToNext();
  };

  const handleCloseClick = () => {
    trackAdGoal(AD_GOALS.INTRO_PLAY_CLICK, {params: {slide: activeSlide?.id}});
    onClose?.();
  };

  const handleDotClick = (index) => {
    sendButtonClickEvent(INTRO_SLIDES_PAGE_ID, 'dot', String(index + 1));
    MENU_SOUND_MANAGER.playNextCarouselItemClick();
    emblaApi?.goTo(index);
  };

  return (
    <div className={`intro-slides-overlay ${isVisible ? 'intro-slides-overlay_visible' : ''}`}>
      {/* Фон всей колоды, а не отдельного слайда: холст лежит в оверлее, вне
          карусели, поэтому при листании искры остаются на месте и анимация не
          едет вместе со слайдами. Красным фон искр делает сам оверлей — холст
          прозрачный и добавляет только свет */}
      <CampfireSparks sparkScale={SPARK_SCALE}/>
      <div className="intro-slides">
        <div className="intro-slides__viewport" ref={emblaRef}>
          <div className="intro-slides__container">
            {INTRO_SLIDES.map((slide) => (
              <div className="intro-slides__slide" key={slide.id}>
                <div className="intro-slide__image-frame">
                  {/* Кадр под раскладку выбирает браузер: на телефоне
                      портретный, на широком экране — горизонтальный */}
                  <picture>
                    <source media="(max-width: 768px)" srcSet={slide.imagePathMobile}/>
                    <img
                      className="intro-slide__image"
                      src={slide.imagePathDesktop}
                      alt={slide.imageAlt}
                    />
                  </picture>
                </div>
                <div className="intro-slide__text">
                  {/* Название игры на каждом слайде: человек пришел по
                      рекламе и должен видеть, что именно он открыл */}
                  <h1 className="intro-slide__brand">Стратегикон</h1>
                  <h2 className="intro-slide__title">{slide.title}</h2>
                  <ul className="intro-slide__bullets">
                    {slide.bullets.map((bullet, bulletIndex) => (
                      <li className="intro-slide__bullet" key={bulletIndex}>
                        {splitTextByAccents(bullet.text, bullet.accents).map((part, partIndex) => (
                          part.isAccent ? (
                            <span className="intro-slide__accent" key={partIndex}>{part.text}</span>
                          ) : (
                            <React.Fragment key={partIndex}>{part.text}</React.Fragment>
                          )
                        ))}
                      </li>
                    ))}
                  </ul>
                  {slide.footnote && (
                    <p className="intro-slide__footnote">{slide.footnote}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="intro-slides__dots">
          {INTRO_SLIDES.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              className={`intro-slides__dot ${index === selectedIndex ? 'intro-slides__dot_active' : ''}`}
              aria-label={`Слайд ${index + 1}`}
              aria-current={index === selectedIndex}
              onClick={() => handleDotClick(index)}
            />
          ))}
        </div>

        <div className="main-menu-buttons intro-slides__buttons">
          <PageButton
            onClick={isLastSlide ? handleCloseClick : handleNextClick}
            pageId={INTRO_SLIDES_PAGE_ID}
            buttonId={isLastSlide ? 'start_playing' : 'next'}
            buttonValue={activeSlide?.id}
          >
            {activeSlide?.buttonLabel}
          </PageButton>
        </div>
      </div>
    </div>
  );
}

export default IntroSlides;
